import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, Column } from '@/components/ui/data-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getAccounts, getAccountsStats, deleteAccount } from '@/api/accounts';
import type { Account, AccountsStats, AccountFilters, UserRole } from '@/types/account';
import { ROLE_LABELS } from '@/types/account';
import {
  Plus,
  FileText,
  Edit,
  MoreHorizontal,
  Users,
  Search,
  Shield,
  Trash2,
  LogIn,
  BookUser,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';
  onClick?: () => void;
  isActive?: boolean;
}

function StatCard({ title, value, icon: Icon, color, onClick, isActive }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  };

  const activeColorClasses = {
    blue: 'ring-2 ring-blue-500',
    green: 'ring-2 ring-emerald-500',
    orange: 'ring-2 ring-orange-500',
    red: 'ring-2 ring-red-500',
    purple: 'ring-2 ring-purple-500',
    gray: 'ring-2 ring-gray-500',
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isActive && activeColorClasses[color]
      )}
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <div className={cn('rounded-lg p-3', colorClasses[color])}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function getRoleBadgeVariant(role: string): 'success' | 'warning' | 'default' | 'destructive' {
  switch (role) {
    case 'ROLE_SUPERADMIN':
      return 'destructive';
    case 'ROLE_ADMIN':
      return 'warning';
    case 'ROLE_RETAILER':
    case 'ROLE_SUPPORT':
      return 'success';
    default:
      return 'default';
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function AccountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [stats, setStats] = useState<AccountsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Account | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const { user, hasRole, hasPermission, impersonate, isImpersonating } = useAuth();

  // Check if current user can impersonate
  const canImpersonate = hasRole('ROLE_SUPERADMIN') && !isImpersonating;
  const canDelete = hasPermission('action.accounts.delete');

  // Handle account deletion (soft delete)
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAccount(deleteTarget.id);
      setDeleteTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  // Handle impersonation
  const handleImpersonate = useCallback(async (account: Account) => {
    try {
      await impersonate(account.id);
      navigate('/');
    } catch (error) {
      console.error('Impersonation failed:', error);
    }
  }, [impersonate, navigate]);

  // Get filters from URL params
  const filters: AccountFilters = useMemo(
    () => ({
      role: searchParams.get('role') || 'all',
      search: searchParams.get('search') || '',
      page: Number(searchParams.get('page')) || 1,
      limit: Number(searchParams.get('limit')) || 25,
      sortBy: searchParams.get('sortBy') || 'lastname',
      sortOrder: (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'ASC',
    }),
    [searchParams]
  );

  // Update filters
  const updateFilters = useCallback(
    (newFilters: Partial<AccountFilters>) => {
      const updated = { ...filters, ...newFilters };
      const params = new URLSearchParams();

      if (updated.role && updated.role !== 'all') params.set('role', updated.role);
      if (updated.search) params.set('search', updated.search);
      if (updated.page && updated.page !== 1) params.set('page', updated.page.toString());
      if (updated.limit && updated.limit !== 25) params.set('limit', updated.limit.toString());
      if (updated.sortBy && updated.sortBy !== 'lastname') params.set('sortBy', updated.sortBy);
      if (updated.sortOrder && updated.sortOrder !== 'ASC') params.set('sortOrder', updated.sortOrder);

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [accountsResponse, statsResponse] = await Promise.all([
          getAccounts(filters),
          getAccountsStats(),
        ]);

        setAccounts(accountsResponse.data);
        setTotalItems(accountsResponse.meta.total);
        setTotalPages(accountsResponse.meta.totalPages);
        setStats(statsResponse);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [filters, refreshKey]);

  // Handle sorting
  const handleSort = (key: string) => {
    const newOrder =
      filters.sortBy === key && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    updateFilters({ sortBy: key, sortOrder: newOrder, page: 1 });
  };

  // Handle search with debounce
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilters({ search: searchInput, page: 1 });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, updateFilters]);

  // Table columns
  const columns: Column<Account>[] = useMemo(
    () => [
      {
        key: 'lastname',
        header: 'Nom',
        sortable: true,
        render: (account) => (
          <div>
            <p className="font-medium text-gray-900">{account.fullname}</p>
            <p className="text-xs text-gray-500">{account.email}</p>
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        render: (account) => (
          <Badge variant={getRoleBadgeVariant(account.primaryRole || '')}>
            {account.roleLabel}
          </Badge>
        ),
      },
      {
        key: 'createdAt',
        header: 'Creation',
        sortable: true,
        render: (account) => (
          <span className="text-sm text-gray-600">
            {formatDate(account.createdAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        className: 'w-10',
        render: (account) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => navigate(`/accounts/${account.id}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              {canImpersonate && account.id !== user?.id && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleImpersonate(account)}
                    className="text-amber-600"
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    Se connecter en tant que
                  </DropdownMenuItem>
                </>
              )}
              {canDelete && account.id !== user?.id && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => setDeleteTarget(account)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [canImpersonate, canDelete, user?.id, handleImpersonate]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <BookUser className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Comptes
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gerez les comptes utilisateurs de la plateforme
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            Exporter
          </Button>
          <Button onClick={() => navigate('/accounts/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau compte
          </Button>
        </div>
      </div>

      {/* Stats Cards — 2 lignes de 4 pour laisser la place aux libellés */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total"
          value={stats?.total || 0}
          icon={Users}
          color="blue"
          onClick={() => updateFilters({ role: 'all', page: 1 })}
          isActive={filters.role === 'all'}
        />
        <StatCard
          title="Administrateur"
          value={stats?.roles?.ROLE_SUPERADMIN || 0}
          icon={Shield}
          color="red"
          onClick={() => updateFilters({ role: 'ROLE_SUPERADMIN', page: 1 })}
          isActive={filters.role === 'ROLE_SUPERADMIN'}
        />
        <StatCard
          title="Lead Dev"
          value={stats?.roles?.ROLE_ADMIN || 0}
          icon={Shield}
          color="orange"
          onClick={() => updateFilters({ role: 'ROLE_ADMIN', page: 1 })}
          isActive={filters.role === 'ROLE_ADMIN'}
        />
        <StatCard
          title="Contributeur"
          value={stats?.roles?.ROLE_USER || 0}
          icon={Users}
          color="purple"
          onClick={() => updateFilters({ role: 'ROLE_USER', page: 1 })}
          isActive={filters.role === 'ROLE_USER'}
        />
        <StatCard
          title="Apporteur"
          value={stats?.roles?.ROLE_RETAILER || 0}
          icon={Users}
          color="green"
          onClick={() => updateFilters({ role: 'ROLE_RETAILER', page: 1 })}
          isActive={filters.role === 'ROLE_RETAILER'}
        />
        <StatCard
          title="Developpeur"
          value={stats?.roles?.ROLE_DEV || 0}
          icon={Users}
          color="gray"
          onClick={() => updateFilters({ role: 'ROLE_DEV', page: 1 })}
          isActive={filters.role === 'ROLE_DEV'}
        />
        <StatCard
          title="Support"
          value={stats?.roles?.ROLE_SUPPORT || 0}
          icon={Users}
          color="orange"
          onClick={() => updateFilters({ role: 'ROLE_SUPPORT', page: 1 })}
          isActive={filters.role === 'ROLE_SUPPORT'}
        />
        <StatCard
          title="Config"
          value={stats?.roles?.ROLE_CONFIG || 0}
          icon={Users}
          color="gray"
          onClick={() => updateFilters({ role: 'ROLE_CONFIG', page: 1 })}
          isActive={filters.role === 'ROLE_CONFIG'}
        />
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search */}
        <div className="relative w-full sm:w-auto sm:min-w-[300px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Select
            value={filters.role}
            onValueChange={(value) => updateFilters({ role: value, page: 1 })}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Filtrer par role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les comptes</SelectItem>
              <SelectItem value="ROLE_SUPERADMIN">Administrateur</SelectItem>
              <SelectItem value="ROLE_ADMIN">Lead developpeur</SelectItem>
              <SelectItem value="ROLE_RETAILER">Apporteur d'affaires</SelectItem>
              <SelectItem value="ROLE_SUPPORT">Support</SelectItem>
              <SelectItem value="ROLE_USER">Contributeur</SelectItem>
              <SelectItem value="ROLE_DEV">Developpeur</SelectItem>
              <SelectItem value="ROLE_CONFIG">Configuration</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={accounts}
        loading={loading}
        page={filters.page}
        pageSize={filters.limit}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={(page) => updateFilters({ page })}
        onPageSizeChange={(limit) => updateFilters({ limit, page: 1 })}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
        keyExtractor={(account) => account.id}
        emptyMessage="Aucun compte trouve"
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le compte{' '}
              <strong>{deleteTarget?.fullname}</strong> ({deleteTarget?.email}) ?
              Il ne pourra plus se connecter à la plateforme.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
